import React, { useRef, useState } from "react";
import { Modal, Button, Form, Tabs, Tab } from "react-bootstrap";
import { FaCalendarAlt, FaImage } from "react-icons/fa";
import InsertProjectImageModal from "../dashboard/employee-dashboard/InsertProjectImageModal";
import { FaBold, FaItalic, FaUnderline, FaStrikethrough } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { useProjects } from "../../context/ProjectContext";

const AddProjectModal = ({ show, handleClose }) => {
  const { addProject } = useProjects();
  const [key, setKey] = useState("basic");
  const [showImageModal, setShowImageModal] = useState(false);
  const [fontSize, setFontSize] = useState("14");
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    client: "",
    priority: "Medium",
    projectValue: "$",
    priceType: "",
    description: "",
    members: "",
    teamLeader: "",
    projectManager: "",
    tags: "",
    status: "Active",
  });
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const handleClick = () => {
    fileRef.current.click(); // 🔥 trigger file input
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size
    if (file.size > 4 * 1024 * 1024) {
      alert("Image must be below 4MB");
      return;
    }

    setPreview(URL.createObjectURL(file));
  };

  const handleCancel = () => {
    setPreview(null);
    fileRef.current.value = ""; // reset input
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      alert("Project name is required");
      return;
    }

    const newProject = {
      title: formData.title,
      name: formData.title,
      desc: formData.description,
      client: formData.client,
      priority: formData.priority,
      projectValue: formData.projectValue,
      priceType: formData.priceType,
      description: formData.description,
      user: formData.teamLeader || "Project Lead",
      leader: formData.teamLeader || "Project Lead",
      role: "Project Leader",
      deadline: endDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      date: endDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      tasks: "0/10",
      img: preview || "/assets/img/users/default-avatar.jpg",
      image: preview || "/assets/img/users/default-avatar.jpg",
      status: formData.status,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      members: formData.members
        ? formData.members.split(",").map((m) => m.trim())
        : [],
      teamLeader: formData.teamLeader,
      projectManager: formData.projectManager,
      tags: formData.tags ? formData.tags.split(",").map((t) => t.trim()) : [],
    };

    addProject(newProject);

    // Reset form
    setFormData({
      title: "",
      client: "",
      priority: "Medium",
      projectValue: "$",
      priceType: "",
      description: "",
      members: "",
      teamLeader: "",
      projectManager: "",
      tags: "",
      status: "Active",
    });
    setPreview(null);
    setKey("basic");
    handleClose();
  };

  const startRef = useRef(null);
  const endRef = useRef(null);
  return (
    <>
      <Modal
        className="mt-2"
        show={show}
        onHide={handleClose}
        size="lg"
        centered
      >
        {/* HEADER */}
        <Modal.Header closeButton>
          <div className="d-flex align-items-center">
            <h5 className="me-2 mb-0">Add Project</h5>
            <small>Project ID : PRO-0004</small>
          </div>
        </Modal.Header>

        {/* TABS */}
        <div className="px-3 pt-2">
          <Tabs
            activeKey={key}
            onSelect={(k) => setKey(k)}
            className="custom-tabs"
          >
            {/* BASIC INFO */}
            <Tab eventKey="basic" title="Basic Information">
              <Modal.Body>
                {/* Upload */}
                <div className="upload-box d-flex align-items-center p-3 mb-3">
                  {/* Left */}
                  <div className="upload-circle me-3">
                    {preview ? (
                      <img
                        src={preview}
                        alt="preview"
                        className="preview-img"
                      />
                    ) : (
                      <FaImage />
                    )}
                  </div>

                  {/* Right */}
                  <div>
                    <h6>Upload Project Logo</h6>
                    <small>Image should be below 4 mb</small>

                    <div className="mt-2">
                      {/* Hidden Input */}
                      <input
                        type="file"
                        ref={fileRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleUpload}
                      />

                      {/* Upload Button */}
                      <Button
                        style={{ backgroundColor: "#ff6b1a" }}
                        size="sm"
                        className="me-2  border-0"
                        onClick={handleClick}
                      >
                        Upload
                      </Button>

                      {/* Cancel */}
                      {preview && (
                        <Button
                          size="sm"
                          variant="light"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* FORM */}
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Name</Form.Label>
                    <Form.Control
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter project name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Client</Form.Label>
                    <Form.Control
                      name="client"
                      value={formData.client}
                      onChange={handleInputChange}
                      placeholder="Enter client name"
                    />
                  </Form.Group>

                  <div className="row">
                    {/* Start Date */}
                    <div className="col-md-6 mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <div className="input-icon">
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          ref={startRef}
                        />
                        <FaCalendarAlt
                          className="calendar-icon"
                          onClick={() => startRef.current.setOpen(true)}
                        />
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="col-md-6 mb-3">
                      <Form.Label>End Date</Form.Label>
                      <div className="input-icon">
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          ref={endRef}
                        />
                        <FaCalendarAlt
                          className="calendar-icon"
                          onClick={() => endRef.current.setOpen(true)}
                        />
                      </div>
                    </div>

                    {/* Priority */}
                    <div className="col-md-4 mb-3">
                      <Form.Label>Priority</Form.Label>
                      <Form.Select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                      >
                        <option>Select</option>
                        <option>High</option>
                        <option>Medium</option>
                        <option>Low</option>
                      </Form.Select>
                    </div>

                    {/* Project Value */}
                    <div className="col-md-4 mb-3">
                      <Form.Label>Project Value</Form.Label>
                      <Form.Control
                        name="projectValue"
                        value={formData.projectValue}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Price Type */}
                    <div className="col-md-4 mb-3">
                      <Form.Label>Price Type</Form.Label>
                      <Form.Control
                        name="priceType"
                        value={formData.priceType}
                        onChange={handleInputChange}
                        placeholder="e.g., Fixed, Hourly"
                      />
                    </div>
                  </div>

                  {/* DESCRIPTION */}
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>

                    <div className="editor-box">
                      {/* Toolbar */}
                      <div className="editor-toolbar">
                        {/* Font Size Dropdown */}
                        <select
                          className="font-select"
                          value={fontSize}
                          onChange={(e) => setFontSize(e.target.value)}
                        >
                          <option value="12">12</option>
                          <option value="14">14</option>
                          <option value="16">16</option>
                          <option value="18">18</option>
                        </select>

                        {/* Icons */}
                        <button>
                          <FaBold />
                        </button>
                        <button>
                          <FaItalic />
                        </button>
                        <button>
                          <FaUnderline />
                        </button>
                        <button>
                          <FaStrikethrough />
                        </button>

                        <button onClick={() => setShowImageModal(true)}>
                          <FaImage />
                        </button>
                      </div>

                      {/* Text Area */}
                      <textarea
                        className="editor-textarea"
                        style={{ fontSize: `${fontSize}px` }}
                        placeholder="Enter description..."
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                      ></textarea>

                      {/* Bottom resize line */}
                      <div className="resize-bar"></div>
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Upload Files</Form.Label>
                    <Form.Control type="file" />
                  </Form.Group>
                </Form>
              </Modal.Body>
            </Tab>

            {/* MEMBERS TAB */}
            <Tab eventKey="members" title="Members">
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Team Members</Form.Label>
                  <Form.Control
                    placeholder="Add members (comma separated)"
                    name="members"
                    value={formData.members}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Team Leader</Form.Label>
                  <Form.Control
                    placeholder="Add team leader"
                    name="teamLeader"
                    value={formData.teamLeader}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Project Manager</Form.Label>
                  <Form.Control
                    placeholder="Add project manager"
                    name="projectManager"
                    value={formData.projectManager}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    placeholder="Add tags (comma separated)"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option>Select</option>
                    <option>Active</option>
                    <option>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Modal.Body>
            </Tab>
          </Tabs>
          <style>
            {`
                    .custom-tabs .nav-link.active {
  color: #ff6a00 !important;
  border-bottom: 2px solid #ff6a00 !important;
}

.upload-box {
  background: #f8f9fa;
  border-radius: 10px;
}

.upload-circle {
  width: 60px;
  height: 60px;
  border: 1px dashed #ccc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-icon {
  position: relative;
}

.input-icon .icon {
  position: absolute;
  right: 10px;
  top: 10px;
  color: gray;
}

.editor-box {
  border: 1px solid #ccc;
}

.editor-toolbar {
  padding: 5px;
  border-bottom: 1px solid #ccc;
}

.editor-textarea {
  width: 100%;
  height: 100px;
  border: none;
}

//  insert design
.editor-box {
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  background: #fff;
}

/* Toolbar */
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-bottom: 1px solid #e5e5e5;
  background: #f7f7f7;
}

/* Dropdown */
.font-select {
  border: 1px solid #ccc;
  padding: 2px 6px;
  height: 28px;
  font-size: 13px;
  border-radius: 3px;
}

/* Buttons */
.editor-toolbar button {
  border: 1px solid #ccc;
  background: #fff;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 13px;
}

.editor-toolbar button:hover {
  background: #f0f0f0;
}

/* Textarea */
.editor-textarea {
  width: 100%;
  height: 120px;
  border: none;
  outline: none;
  padding: 10px;
  resize: none;
}

/* Bottom drag line */
.resize-bar {
  height: 8px;
  background: #f1f1f1;
  border-top: 1px solid #ddd;
  cursor: ns-resize;
}

.upload-box {
  border: 1px dashed #dcdcdc;
  border-radius: 6px;
  background: #fafafa;
}

/* Circle */
.upload-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f1f1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #999;
  overflow: hidden;
}

/* Preview Image */
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}


.input-icon {
  position: relative;
}

.input-icon .form-control {
  padding-right: 35px;
}
  
.input-icon {
  position: relative;
  width: 100%;
}

/* Input styling */
.custom-date-input {
  width: 100%;
  padding-right: 40px; /* space for icon */
}

/* Calendar icon EXACT right inside */
.calendar-icon {
  position: absolute;
  right: 140px; /* 🔥 pushes inside properly */
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #888;
  cursor: pointer;
  pointer-events: auto;
}

/* Optional: hover effect */
.calendar-icon:hover {
  color: #000;
}
.input-icon .icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  cursor: pointer;
}
.cursor {
  cursor: pointer;
}`}
          </style>
        </div>

        {/* FOOTER */}
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#ff6b1a" }}
            className="border-0"
            onClick={handleSave}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* IMAGE MODAL */}
      <InsertProjectImageModal
        show={showImageModal}
        handleClose={() => setShowImageModal(false)}
      />
    </>
  );
};

export default AddProjectModal;
